import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import styles from './AudioPlayer.module.scss'
import {quiesTrack, type AudioContext, type AudioStopReason} from '@product/quies/utils/analytics'
import {postListening} from '@product/quies/utils/listeningLedger'
import {createListeningTracker, type ListenReport} from './listening'
import {useInterfaceStrings} from '@product/quies/i18n/catalogue'
import {Icon} from '@ui/Icon/icon'
import {EyesClosed} from '@product/quies/components/EyesClosed/EyesClosed'
import {trace, mediaState, type TraceData} from '@product/quies/utils/audioTrace'
import {newElementId, streamUrlForElement} from '@product/quies/utils/audioStreamUrl'

// ============================================================================
// useAudioSource — gestion du basculement stream → S3
// ============================================================================
//
// Stratégie : basculer du stream HTTP vers le fichier S3 seulement à un
// instant déjà silencieux — pause, fin, seek, erreur, tampon à sec, stream
// jamais démarré — jamais pendant une lecture audible. Le swap passe par audio.load(), qui coupe le son ~0,5 s ; fait en
// cours de lecture il s'entendait (test prod 2026-04-18, swap proactif). Or
// le stream arrive bien plus vite que le temps réel, donc quand le fichier S3
// existe le navigateur a déjà tout le flux en tampon : le fichier ne sert
// qu'à seeker, rejouer ou survivre à une erreur. Instrumenté via
// audio_swapped {trigger}.
//
// Le swap est effectué sur l'élément <audio> existant (pas de remontage)
// pour préserver la position de lecture.

type AudioSource =
    | {type: 'stream'; url: string}
    | {type: 'file'; url: string}

function resolveInitialSource(streamUrl: string | undefined, fileUrl: string | undefined): AudioSource | null {
    if (streamUrl) return {type: 'stream', url: streamUrl}
    if (fileUrl) return {type: 'file', url: fileUrl}
    return null
}

export function canSwapToFile(sourceType: AudioSource['type'] | undefined, isSwapping: boolean, hasFileUrl: boolean): boolean {
    return sourceType === 'stream' && !isSwapping && hasFileUrl
}

/** `nostart` : rien n'a encore été décodé (readyState < HAVE_CURRENT_DATA), donc rien n'est
 *  audible — le swap immédiat ne coupe aucun son. */
export function shouldSwapOnNoStart(readyState: number): boolean {
    return readyState < 2
}

/** A stream `ended` within this many seconds of the file's length is the meditation's true end. */
const NATURAL_END_TOLERANCE_SECONDS = 1

export type StreamEndVerdict = 'natural' | 'premature' | 'undecided'

/** A stream's `ended` is the element's buffered edge, not the voicing's end: Safari plays what it
 *  holds when a re-request is refused, and fires `ended` there (prod 2026-10-07, 10-19). Only a
 *  position at the file's real length is the end; any earlier one is premature. Without the file's
 *  length the verdict waits. */
export function judgeStreamEnd(position: number, fileDuration: number): StreamEndVerdict {
    if (!(fileDuration > 0)) return 'undecided'
    return position >= fileDuration - NATURAL_END_TOLERANCE_SECONDS ? 'natural' : 'premature'
}

/** The same file URL under a new query string, for a fetch that skips what a cache holds of a failure. */
export function withCacheBust(url: string, token: number | string): string {
    return `${url}${url.includes('?') ? '&' : '?'}_r=${token}`
}

/** What the person or the element did to cause a swap, for the trace. */
type SwapTrigger = 'ended' | 'pause' | 'seek' | 'stall' | 'error' | 'nostart' | 'play'

interface UseAudioSourceOptions {
    streamUrl: string | undefined
    fileUrl: string | undefined
    /** The file's length from its own metadata, 0 until known. */
    fileDuration: number
    audioRef: React.RefObject<HTMLAudioElement | null>
    onError: () => void
    /** Swaps go to the audio trace. */
    traced: boolean
}

function useAudioSource({streamUrl, fileUrl, fileDuration, audioRef, onError, traced}: UseAudioSourceOptions) {
    const [source, setSource] = useState<AudioSource | null>(() => resolveInitialSource(streamUrl, fileUrl))
    // Where the stream ended while no verdict could yet be reached (the file or its length unknown).
    const endedAtRef = useRef<number | null>(null)
    // Position à restaurer après chargement du fichier S3
    const seekTargetRef = useRef<number | null>(null)
    // Si la lecture était en cours avant le swap
    const wasPlayingRef = useRef(false)

    // Mettre à jour la source initiale si les props arrivent après le montage
    useEffect(() => {
        setSource(prev => {
            if (prev) return prev
            return resolveInitialSource(streamUrl, fileUrl)
        })
    }, [streamUrl, fileUrl])

    const fileUrlRef = useRef(fileUrl)
    useEffect(() => { fileUrlRef.current = fileUrl }, [fileUrl])
    // Vrai entre audio.load() et loadedmetadata : les événements pause/seeking
    // émis par le rechargement lui-même ne doivent pas redéclencher un swap.
    const swappingRef = useRef(false)

    const swapToFile = useCallback((targetTime: number, resume: boolean, trigger: SwapTrigger, extra: TraceData = {}) => {
        const url = fileUrlRef.current
        if (!url || !audioRef.current) return
        const audio = audioRef.current
        if (traced) trace('swap', {trigger, target: Math.round(targetTime * 100) / 100, resume, ...extra, ...mediaState(audio)})
        swappingRef.current = true
        wasPlayingRef.current = resume
        seekTargetRef.current = targetTime
        setSource({type: 'file', url})
        // Muter src et recharger — loadedmetadata restaure la position
        audio.src = url
        audio.load()
    }, [audioRef, traced])

    // Swap possible seulement depuis le stream, hors rechargement, fichier connu.
    const canSwap = useCallback(
        () => canSwapToFile(source?.type, swappingRef.current, !!fileUrlRef.current) && !!audioRef.current,
        [source, audioRef],
    )

    // Fichier S3 disponible alors que le stream n'a encore rien décodé (readyState
    // < HAVE_CURRENT_DATA) : rien ne joue, donc rien à couper — on bascule tout de
    // suite. C'est le cas « stream jamais démarré » où le swap proactif servait de
    // secours ; il reste couvert. Un stream qui joue n'est pas touché.
    useEffect(() => {
        if (!fileUrl || !canSwap()) return
        const audio = audioRef.current!
        if (!shouldSwapOnNoStart(audio.readyState)) return
        quiesTrack('audio_swapped', {at_seconds: Math.round(audio.currentTime), was_playing: !audio.paused, trigger: 'nostart'})
        swapToFile(audio.currentTime, !audio.paused, 'nostart')
    }, [fileUrl, canSwap, audioRef, swapToFile])

    // Tampon à sec en cours de lecture : déjà silencieux, on bascule et on reprend.
    const handleWaiting = useCallback(() => {
        if (!canSwap()) return
        const audio = audioRef.current!
        if (audio.paused) return
        quiesTrack('audio_swapped', {at_seconds: Math.round(audio.currentTime), was_playing: true, trigger: 'stall'})
        swapToFile(audio.currentTime, true, 'stall')
    }, [canSwap, audioRef, swapToFile])

    // Pause utilisateur : instant silencieux, on bascule sans reprendre la lecture.
    // La pause qui précède `ended` est laissée au handler de fin.
    const handlePause = useCallback(() => {
        if (!canSwap()) return
        const audio = audioRef.current!
        if (audio.ended) return
        quiesTrack('audio_swapped', {at_seconds: Math.round(audio.currentTime), was_playing: false, trigger: 'pause'})
        swapToFile(audio.currentTime, false, 'pause')
    }, [canSwap, audioRef, swapToFile])

    // Seek utilisateur : le saut est déjà une coupure ; le fichier S3 offre le
    // seek complet là où le stream chunked ne l'offre pas (pas de Range).
    const handleSeeking = useCallback(() => {
        if (!canSwap()) return
        const audio = audioRef.current!
        quiesTrack('audio_swapped', {at_seconds: Math.round(audio.currentTime), was_playing: !audio.paused, trigger: 'seek'})
        swapToFile(audio.currentTime, !audio.paused, 'seek')
    }, [canSwap, audioRef, swapToFile])

    const handleStreamError = useCallback(() => {
        if (source?.type !== 'stream') {
            onError()
            return
        }
        const audio = audioRef.current
        const t = audio?.currentTime ?? 0
        const playing = audio ? !audio.paused : false
        if (fileUrlRef.current) {
            quiesTrack('audio_stream_error_swap', {at_seconds: Math.round(t), was_playing: playing})
            swapToFile(t, playing, 'error')
        } else {
            onError()
        }
    }, [source, audioRef, swapToFile, onError])

    // Seek demandé depuis le rail ou l'écran verrouillé. Sur le stream, le seek est lui-même
    // l'instant silencieux : on bascule directement à la cible — le stream chunked n'a pas de
    // plages où chercher, donc audio.currentTime y serait ramené à ce qui est en tampon.
    const seekTo = useCallback((target: number) => {
        const audio = audioRef.current
        if (!audio) return
        if (!canSwap()) {
            audio.currentTime = target
            return
        }
        quiesTrack('audio_swapped', {at_seconds: Math.round(target), was_playing: !audio.paused, trigger: 'seek'})
        swapToFile(target, !audio.paused, 'seek')
    }, [canSwap, audioRef, swapToFile])

    // Fin du stream. Un `ended` de stream n'est pas la fin de la méditation : c'est le bord du tampon
    // quand le navigateur n'obtient plus la suite. Dès que la longueur du fichier est connue, on la
    // compare à la position : proche de la fin, c'est la fin naturelle (fichier à 0, sans reprendre —
    // relecture et seek se font alors sur le fichier) ; plus tôt, le fichier reprend à la position et
    // la lecture continue. Sans fichier ni longueur encore, la position est gardée et le verdict suit
    // leur arrivée.
    const settleEnd = useCallback(() => {
        const at = endedAtRef.current
        if (at === null || !canSwap()) return
        const verdict = judgeStreamEnd(at, fileDuration)
        if (verdict === 'undecided') return
        endedAtRef.current = null
        const premature = verdict === 'premature'
        quiesTrack('audio_swapped', {at_seconds: Math.round(premature ? at : 0), was_playing: premature, trigger: 'ended'})
        swapToFile(premature ? at : 0, premature, 'ended', {premature, fileDur: Math.round(fileDuration * 100) / 100})
    }, [canSwap, fileDuration, swapToFile])

    const handleStreamEnded = useCallback(() => {
        if (source?.type !== 'stream') return
        endedAtRef.current = audioRef.current?.currentTime ?? 0
        settleEnd()
    }, [source, audioRef, settleEnd])

    // The file or its length arrived after the stream ended.
    useEffect(settleEnd, [fileUrl, fileDuration, settleEnd])

    // L'utilisateur appuie sur play alors que le stream s'est arrêté sans verdict : le fichier reprend
    // à la position où il s'est arrêté.
    const handlePlayAttempt = useCallback(() => {
        const at = endedAtRef.current
        if (at !== null && canSwap()) {
            endedAtRef.current = null
            swapToFile(at, true, 'play')
        }
    }, [canSwap, swapToFile])

    // Après chargement du nouvel élément, restaurer la position et reprendre si nécessaire
    const handleLoadedMetadata = useCallback(() => {
        const audio = audioRef.current
        if (!audio) return
        swappingRef.current = false
        if (seekTargetRef.current !== null) {
            audio.currentTime = seekTargetRef.current
            seekTargetRef.current = null
        }
        if (wasPlayingRef.current) {
            wasPlayingRef.current = false
            audio.play().catch(() => {})
        }
    }, [audioRef])

    return {
        source,
        isSwapping: () => swappingRef.current,
        handleStreamError,
        handleStreamEnded,
        handlePlayAttempt,
        handlePause,
        handleSeeking,
        handleWaiting,
        handleLoadedMetadata,
        seekTo,
    }
}

// ============================================================================
// Time formatting (AudioPlayer spec § Anatomy: "[m minutes s of M minutes S]")
// ============================================================================

/** The seek rail's length in seconds, 0 while it cannot seek. Seek exists once the file is known
 *  (decisions.md §22): on the live stream the element's duration is Infinity, so the file's own
 *  metadata gives the length; on the file (or a plain `src`) the element's does. */
export function seekableLength(fileKnown: boolean, onStream: boolean, elementDuration: number, fileDuration: number): number {
    if (!fileKnown) return 0
    return onStream ? fileDuration : elementDuration
}

/** Seconds of audio the element holds beyond its position. */
export function bufferedAhead(audio: Pick<HTMLAudioElement, 'buffered' | 'currentTime'>): number {
    const {buffered} = audio
    if (!buffered.length) return 0
    return Math.max(0, buffered.end(buffered.length - 1) - audio.currentTime)
}

const TRACED_MEDIA_EVENTS = [
    'loadstart', 'loadedmetadata', 'loadeddata', 'durationchange', 'canplay', 'canplaythrough', 'play', 'playing',
    'pause', 'waiting', 'stalled', 'suspend', 'abort', 'emptied', 'error', 'ended', 'seeking', 'seeked', 'timeupdate',
] as const

/** A stall, or a stream that goes quiet, is reported once it has lasted this long. */
const STOP_REPORT_AFTER_MS = 10_000

export function formatClock(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m}:${String(s).padStart(2, '0')}`
}

export function seekValueText(currentSeconds: number, totalSeconds: number): string {
    const cm = Math.floor(currentSeconds / 60)
    const cs = Math.floor(currentSeconds % 60)
    const tm = Math.floor(totalSeconds / 60)
    const ts = Math.floor(totalSeconds % 60)
    return `${cm} minutes ${cs} of ${tm} minutes ${ts}`
}

// ============================================================================
// AudioPlayer
// ============================================================================

interface AudioPlayerProps {
    // Mode simple (historique) : une seule URL connue
    src?: string
    // Mode actif : stream en cours + URL S3 disponible après audio_complete
    streamUrl?: string
    fileUrl?: string
    autoPlay?: boolean
    title?: string
    /** The meditation started or stopped (pause or end) — the Continue gate reads it. */
    onPlayingChange?: (playing: boolean) => void
    /** Played to its end. */
    onEnded?: () => void
    /** The voicing is whole (the machine saw `audio_complete`): a live stream that ends is the whole
     *  meditation. Without it, a file known to the player says as much. */
    voicingComplete?: boolean
    /** Eyes-closed mode, held by the host, which makes its page inert behind the veil. Without a
     *  handler the player offers no eyes-closed mode (a sample: condition spec § States). */
    eyesClosed?: boolean
    onEyesClosedChange?: (closed: boolean) => void
    /** A past meditation, in History: it docks only once played. */
    past?: boolean
    /** The saved file will not play, after one silent re-fetch (Failures spec, cases 12, 13): the
     *  host shows its failure in the player's place. Without it, the player's own Retry. */
    onUnplayable?: () => void
    /** Where it plays and which meditation it is: every audio event carries the surface. */
    analytics: AudioContext
}

/** The dock shows while the player is off screen and not veiled; a past meditation's only once it
 *  has been played (History spec: "Playing a past meditation lights it and docks it"). Docked on
 *  scroll alone, a past player's bar stood over the present turn's options (prod, 2026-09-28). */
export function showsDock({offscreen, eyesClosed, past, played}: {offscreen: boolean; eyesClosed: boolean; past: boolean; played: boolean}): boolean {
    return offscreen && !eyesClosed && (!past || played)
}

export function AudioPlayer({src, streamUrl, fileUrl, autoPlay, title, onPlayingChange, onEnded, voicingComplete = false, eyesClosed = false, onEyesClosedChange, past = false, onUnplayable, analytics}: AudioPlayerProps) {
    const {t} = useInterfaceStrings()
    const listeningRef = useRef<{item: string; tracker: ReturnType<typeof createListeningTracker>} | null>(null)
    if (listeningRef.current?.item !== analytics.item) {
        const report = analytics.surface === 'session' ? (r: ListenReport) => postListening(analytics.sessionId, r) : undefined
        listeningRef.current = {item: analytics.item, tracker: createListeningTracker(analytics, quiesTrack, report)}
    }
    const listening = listeningRef.current.tracker
    const audioRef = useRef<HTMLAudioElement>(null)
    // Samples play to visitors on public pages: only the session's and History's players are traced.
    const traced = analytics.surface !== 'sample'
    const containerRef = useRef<HTMLDivElement>(null)
    const [error, setError] = useState(false)
    const [retryCount, setRetryCount] = useState(0)
    // One per mounted <audio> (a retry mounts a new one): the stream endpoint answers an element's
    // range-less GET from byte 0 once, never again.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a retry is the new element
    const elementId = useMemo(() => newElementId(), [retryCount])
    const [paused, setPaused] = useState(!autoPlay)
    const [ended, setEnded] = useState(false)
    const [buffering, setBuffering] = useState(false)
    const [refused, setRefused] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [fileDuration, setFileDuration] = useState(0)
    const [offscreen, setOffscreen] = useState(false)
    const [played, setPlayed] = useState(false)
    const docked = showsDock({offscreen, eyesClosed, past, played})
    const playButtonRef = useRef<HTMLButtonElement>(null)
    const eyesButtonRef = useRef<HTMLButtonElement>(null)
    const dockEyesRef = useRef<HTMLButtonElement>(null)
    const wasVeiledRef = useRef(false)

    // Mode simple (src seul) : pas de logique de swap
    const simpleMode = !streamUrl && !fileUrl && !!src
    const knownFileUrl = simpleMode ? undefined : (fileUrl ?? src)

    // A file that fails is fetched once more, silently; failing again it is the host's to say
    // (onUnplayable), or the player's own Retry. A stream's failure is the player's own.
    const onFileRef = useRef(simpleMode)
    // True between a retry's own audio.load() and its loadedmetadata: the pause it causes is not the person's.
    const reloadingRef = useRef(false)
    // True from `pagehide` to `pageshow`: the pause an unloading page's element reports is not the person's either.
    const unloadingRef = useRef(false)
    const watchRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const reportedStopRef = useRef<AudioStopReason | null>(null)
    const refetchedRef = useRef(false)
    const fail = () => {
        const audio = audioRef.current
        if (traced) trace('fileerror', {surface: analytics.surface, retried: refetchedRef.current, ...mediaState(audio ?? null)})
        if (onFileRef.current && audio && !refetchedRef.current) {
            refetchedRef.current = true
            reloadingRef.current = true
            audio.src = withCacheBust(audio.currentSrc || audio.src, Date.now())
            audio.load()
            return
        }
        if (traced) trace('failed', {surface: analytics.surface, ...mediaState(audio ?? null)})
        listening.failed({
            position: audio?.currentTime ?? 0,
            source: onFileRef.current ? 'file' : 'stream',
            code: audio?.error?.code ?? 0,
            networkState: audio?.networkState ?? 0,
        })
        if (onFileRef.current && onUnplayable) onUnplayable()
        else setError(true)
    }

    const {
        source,
        isSwapping,
        handleStreamError,
        handleStreamEnded,
        handlePlayAttempt,
        handlePause,
        handleSeeking,
        handleWaiting,
        handleLoadedMetadata,
        seekTo,
    } = useAudioSource({
        streamUrl: simpleMode ? undefined : streamUrl,
        fileUrl: knownFileUrl,
        fileDuration,
        audioRef,
        onError: fail,
        traced,
    })
    onFileRef.current = simpleMode || source?.type === 'file'

    const stopFacts = (audio: HTMLAudioElement) => ({
        position: audio.currentTime,
        bufferedSeconds: bufferedAhead(audio),
        source: onFileRef.current ? 'file' as const : 'stream' as const,
    })
    const clearWatch = () => {
        if (watchRef.current) clearTimeout(watchRef.current)
        watchRef.current = null
    }
    // A stall that does not recover, or a stream that goes quiet, while the person has not paused:
    // reported once the position has stayed put for STOP_REPORT_AFTER_MS.
    const watchStop = (reason: Exclude<AudioStopReason, 'stream_ended_early'>) => {
        const audio = audioRef.current
        if (!audio || audio.paused || audio.ended || watchRef.current) return
        const from = audio.currentTime
        watchRef.current = setTimeout(() => {
            watchRef.current = null
            const now = audioRef.current
            if (!now || now.paused || now.ended || now.currentTime !== from) return
            const named: AudioStopReason = now.currentTime === 0 ? 'not_started' : reason
            if (reportedStopRef.current === named) return
            reportedStopRef.current = named
            listening.stopped(named, stopFacts(now))
        }, STOP_REPORT_AFTER_MS)
    }
    useEffect(() => clearWatch, [])

    // The page hiding or leaving keeps the record and tells the ledger; the pause an unloading page's
    // element reports is its own.
    useEffect(() => {
        const flush = () => listening.flush(audioRef.current?.currentTime ?? 0, performance.now())
        const onPageHide = () => { unloadingRef.current = true; flush() }
        const onPageShow = () => { unloadingRef.current = false }
        const onVisibility = () => { if (document.hidden) flush() }
        window.addEventListener('pagehide', onPageHide)
        window.addEventListener('pageshow', onPageShow)
        document.addEventListener('visibilitychange', onVisibility)
        return () => {
            window.removeEventListener('pagehide', onPageHide)
            window.removeEventListener('pageshow', onPageShow)
            document.removeEventListener('visibilitychange', onVisibility)
        }
    }, [listening])

    // The audio trace: every media event with the element's state, timeupdate every 5 s. Re-attached
    // when the element is re-created (the error branch unmounts it).
    useEffect(() => {
        const audio = audioRef.current
        if (!traced || !audio) return
        const who = {surface: analytics.surface, stream: streamUrl?.split('/').pop()?.slice(0, 8) ?? null}
        trace('player', {...who, mount: true, file: !!knownFileUrl, autoPlay: !!autoPlay, ...mediaState(audio)})
        let lastTick = 0
        const onEvent = (e: Event) => {
            if (e.type === 'timeupdate') {
                if (e.timeStamp - lastTick < 5_000) return
                lastTick = e.timeStamp
            }
            trace('media', {...who, event: e.type, ...mediaState(audio)})
        }
        for (const type of TRACED_MEDIA_EVENTS) audio.addEventListener(type, onEvent)
        return () => {
            for (const type of TRACED_MEDIA_EVENTS) audio.removeEventListener(type, onEvent)
            trace('player', {...who, unmount: true, ...mediaState(audio)})
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount and unmount of the element only
    }, [traced, error])

    // What the host hands the player over time: the file's arrival, the voicing's end.
    useEffect(() => {
        if (traced) trace('props', {surface: analytics.surface, file: !!knownFileUrl, voicingComplete, autoPlay: !!autoPlay, ...mediaState(audioRef.current)})
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a change of these props is the event
    }, [traced, knownFileUrl, voicingComplete, autoPlay])

    // Lecture automatique au montage. `paused` starts false (no flash of a play glyph), so a
    // rejected play() must set it back: NotAllowedError is the refused state (no gesture, e.g.
    // after a reattach); an AbortError is a reload overtaking the play (a swap at mount), which
    // leaves a plain paused player — seen 2026-09-28 as a "Pause" button over a silent element.
    // A rejected play() fires no `pause` event (nothing played): this is the parent's only report.
    useEffect(() => {
        if (autoPlay && audioRef.current?.paused) {
            audioRef.current.play().then(() => {
                if (traced) trace('autoplay', {ok: true})
                setRefused(false)
            }).catch((e: unknown) => {
                if (traced) trace('autoplay', {ok: false, reason: e instanceof DOMException ? e.name : String(e)})
                if (audioRef.current?.paused) setPaused(true)
                if (e instanceof DOMException && e.name === 'NotAllowedError') setRefused(true)
                onPlayingChange?.(false)
            })
        }
    }, [autoPlay, onPlayingChange])

    // A failure while veiled lifts the veil onto the failed player (spec § Eyes-closed mode).
    useEffect(() => {
        if (error && eyesClosed) onEyesClosedChange?.(false)
    }, [error, eyesClosed, onEyesClosedChange])

    // Leaving the veil returns focus to the [Eyes closed] that is on screen (spec § Eyes-closed mode).
    useEffect(() => {
        if (eyesClosed) {
            wasVeiledRef.current = true
            return
        }
        if (!wasVeiledRef.current) return
        wasVeiledRef.current = false
        ;(docked ? dockEyesRef : eyesButtonRef).current?.focus()
    }, [eyesClosed, docked])

    // While the stream plays, the file's metadata alone gives the seek rail its length.
    const onStream = source?.type === 'stream'
    useEffect(() => {
        if (!knownFileUrl || !onStream) return
        const probe = new Audio()
        probe.preload = 'metadata'
        const onMeta = () => setFileDuration(Number.isFinite(probe.duration) ? probe.duration : 0)
        probe.addEventListener('loadedmetadata', onMeta)
        probe.src = knownFileUrl
        return () => {
            probe.removeEventListener('loadedmetadata', onMeta)
            probe.removeAttribute('src')
            probe.load()
        }
    }, [knownFileUrl, onStream])

    // The dock: appears once the in-page player leaves the viewport (spec § Anatomy).
    useEffect(() => {
        const el = containerRef.current
        if (!el || typeof IntersectionObserver === 'undefined') return
        const obs = new IntersectionObserver(([entry]) => setOffscreen(!entry.isIntersecting), {threshold: 0})
        obs.observe(el)
        return () => obs.disconnect()
    }, [])

    const railLength = seekableLength(simpleMode || !!knownFileUrl, onStream, duration, fileDuration)
    const seekable = railLength > 0

    useEffect(() => {
        if (!('mediaSession' in navigator)) return
        navigator.mediaSession.metadata = new MediaMetadata({
            title: title || t.player.untitled,
            artist: 'Quies',
            artwork: [{src: '/quies-light-512.png', sizes: '512x512', type: 'image/png'}],
        })
        navigator.mediaSession.setActionHandler('play', () => {
            if (traced) trace('mediasession', {command: 'play'})
            audioRef.current?.play()
        })
        navigator.mediaSession.setActionHandler('pause', () => {
            if (traced) trace('mediasession', {command: 'pause'})
            audioRef.current?.pause()
        })
        navigator.mediaSession.setActionHandler('seekto', seekable
            ? (details) => { if (details.seekTime != null) seekTo(details.seekTime) }
            : null)
        return () => {
            navigator.mediaSession.metadata = null
            navigator.mediaSession.setActionHandler('play', null)
            navigator.mediaSession.setActionHandler('pause', null)
            navigator.mediaSession.setActionHandler('seekto', null)
        }
    }, [title, t.player.untitled, seekable, seekTo])

    const togglePlay = useCallback(() => {
        const audio = audioRef.current
        if (!audio) return
        if (audio.ended) {
            audio.currentTime = 0
            audio.play().catch(() => {})
            return
        }
        if (audio.paused) audio.play().catch(() => setRefused(true))
        else audio.pause()
    }, [])

    // A refused autoplay hands the light the focus (spec § States: "takes focus").
    const isRefused = refused && paused && !ended
    useEffect(() => {
        if (isRefused) playButtonRef.current?.focus({preventScroll: true})
    }, [isRefused])

    if (error) {
        return (
            <div className={styles.container}>
                <p className={styles.status}>{t.player.unavailable}</p>
                <button
                    className={styles.retry}
                    onClick={() => {
                        if (traced) trace('retry', {surface: analytics.surface})
                        setError(false)
                        setRetryCount(c => c + 1)
                    }}
                >
                    Retry
                </button>
            </div>
        )
    }

    const resolvedSrc = simpleMode
        ? (retryCount > 0 ? `${src}?r=${retryCount}` : src!)
        : source?.type === 'stream' ? streamUrlForElement(source.url, elementId) : (source?.url ?? src ?? '')

    const phase: 'refused' | 'buffering' | 'ended' | 'paused' | 'playing' =
        ended ? 'ended' : isRefused ? 'refused' : buffering && !paused ? 'buffering' : paused ? 'paused' : 'playing'

    const playLabel = phase === 'ended' ? t.player.playAgain : phase === 'playing' || phase === 'buffering' ? t.player.pause : t.player.play
    const playIcon = phase === 'playing' || phase === 'buffering' ? 'qs-pause-fill' : (phase === 'ended' ? 'qs-retry' : 'qs-play-fill')

    return (
        <div ref={containerRef} className={styles.container} role="group" aria-label={title || t.player.untitled}>
            <audio
                key={simpleMode ? retryCount : undefined}
                ref={audioRef}
                src={resolvedSrc}
                className={styles.hiddenAudio}
                onPlay={() => {
                    setPaused(false)
                    setEnded(false)
                    setRefused(false)
                    setPlayed(true)
                    onPlayingChange?.(true)
                    if (!simpleMode) handlePlayAttempt()
                }}
                onPause={(e) => {
                    clearWatch()
                    listening.pause(e.currentTarget.currentTime, {
                        ended: e.currentTarget.ended,
                        reloading: isSwapping() || reloadingRef.current,
                        unloading: unloadingRef.current,
                    })
                    setPaused(true)
                    // A stream→file swap reloads the element, which pauses it; the meditation goes on.
                    if (!isSwapping()) onPlayingChange?.(false)
                    if (!simpleMode) handlePause()
                }}
                onEnded={(e) => {
                    clearWatch()
                    const audio = e.currentTarget
                    const endVerdict = onStream && !simpleMode ? judgeStreamEnd(audio.currentTime, fileDuration) : null
                    if (traced && endVerdict) trace('streamended', {verdict: endVerdict, premature: endVerdict === 'premature', fileDur: Math.round(fileDuration * 100) / 100, ...mediaState(audio)})
                    if (endVerdict === 'premature') {
                        // The buffered edge, not the end: the file takes over at this position (handleStreamEnded).
                        listening.interrupt()
                        handleStreamEnded()
                        return
                    }
                    const verdict = listening.ended({
                        onStream,
                        voicingComplete: voicingComplete || !!knownFileUrl,
                        position: e.currentTarget.currentTime,
                        lengthSeconds: railLength,
                    })
                    if (verdict === 'stopped') listening.stopped('stream_ended_early', stopFacts(e.currentTarget))
                    setEnded(true)
                    setPaused(true)
                    onPlayingChange?.(false)
                    onEnded?.()
                    if (!simpleMode) handleStreamEnded()
                }}
                onWaiting={() => { listening.interrupt(); setBuffering(true); watchStop('stalled'); if (!simpleMode) handleWaiting() }}
                onStalled={() => watchStop('stalled')}
                onSuspend={() => watchStop('suspended')}
                onAbort={() => watchStop('suspended')}
                onPlaying={(e) => {
                    clearWatch()
                    reportedStopRef.current = null
                    setBuffering(false)
                    listening.playing(e.currentTarget.currentTime, performance.now())
                }}
                onCanPlay={() => setBuffering(false)}
                onTimeUpdate={(e) => {
                    setCurrentTime(e.currentTarget.currentTime)
                    if (!e.currentTarget.paused) listening.time(e.currentTarget.currentTime, performance.now(), railLength)
                }}
                onEmptied={() => { clearWatch(); listening.interrupt() }}
                onDurationChange={(e) => setDuration(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0)}
                onError={simpleMode ? fail : handleStreamError}
                onSeeking={() => {
                    listening.interrupt()
                    if (!simpleMode) handleSeeking()
                }}
                onLoadedMetadata={(e) => {
                    reloadingRef.current = false
                    setDuration(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0)
                    if (!simpleMode) handleLoadedMetadata()
                }}
            />

            <button
                ref={playButtonRef}
                type="button"
                className={styles.playButton}
                data-phase={phase}
                aria-label={playLabel}
                aria-busy={phase === 'buffering'}
                onClick={togglePlay}
            >
                <Icon id={playIcon} size={28} className={styles.playIcon} />
            </button>

            {title && <p className={styles.title}>{title}</p>}

            <div className={styles.progress}>
                <span className={styles.time}>{formatClock(currentTime)}</span>
                <input
                    type="range"
                    aria-label={t.player.seek}
                    aria-disabled={!seekable}
                    aria-valuetext={seekable ? seekValueText(currentTime, railLength) : undefined}
                    className={seekable ? styles.rail : styles.railDisabled}
                    style={seekable ? {'--fill': `${Math.min(100, (currentTime / railLength) * 100)}%`} as React.CSSProperties : undefined}
                    min={0}
                    max={seekable ? railLength : 100}
                    step={10}
                    value={seekable ? currentTime : 0}
                    disabled={!seekable}
                    onChange={(e) => seekTo(Number(e.target.value))}
                />
                <span className={styles.time}>{seekable ? formatClock(railLength) : '—:—'}</span>
            </div>

            {phase === 'refused' && <p className={styles.hint}>{t.failure.tapToListen}</p>}

            {onEyesClosedChange && (
                <button ref={eyesButtonRef} type="button" className={styles.eyesButton} onClick={() => onEyesClosedChange(true)}>
                    <Icon id="qs-eyes-closed" size={18} outline />
                    <span>{t.player.eyesClosed}</span>
                </button>
            )}

            {docked && (
                <div className={styles.dock}>
                    <span className={styles.dockTitle}>{title}</span>
                    <button type="button" className={styles.dockPlay} aria-label={playLabel} onClick={togglePlay}>
                        <Icon id={playIcon} size={18} />
                    </button>
                    {onEyesClosedChange && (
                        <button ref={dockEyesRef} type="button" className={styles.dockEyes} aria-label={t.player.eyesClosed} onClick={() => onEyesClosedChange(true)}>
                            <Icon id="qs-eyes-closed" size={18} outline />
                        </button>
                    )}
                </div>
            )}

            {eyesClosed && (
                <EyesClosed
                    paused={paused}
                    ended={ended}
                    onToggle={togglePlay}
                    onExit={() => onEyesClosedChange?.(false)}
                />
            )}
        </div>
    )
}
